import { Interweave } from 'interweave';
import React, { FC, useEffect, useState } from 'react';
import AnimateHeight from 'react-animate-height';
import { getQueryParam, setQueryString } from '../../helpers/url/urlHelpers';
import RichTextStyleWrapper from '../base/richTextStyleWrapper';
import { H3 } from '../base/typography';
import * as StyledAccordion from './accordion.styles';

export type IAccordionItem = {
	Values: {
		PageTitle: string;
		Title: string;
		Content: string;
	};
	urlRedirect?: string;
	id: number;
};

interface Props {
	title?: string;
	items: IAccordionItem[];
	buttonOnClick?: any;
	buttonString?: string;
	highlightId?: number;
}

const scrollToElement = element => {
	if (element === null) return;
	let ly = element.getBoundingClientRect().y;
	if (ly > 90) ly -= 90; // offset for navigation bar
	if (ly < 0) ly = 0;

	window.scrollTo(0, ly);
};

const Accordion: FC<Props> = ({ title, items, buttonString, buttonOnClick, highlightId }) => {
	const [activeItem, setActiveItem]: [number, any] = useState(highlightId);

	useEffect(() => {
		if (items && items.length > 0) {
			if (highlightId) {
				scrollToElement(document.getElementById(String(highlightId)));
			} else {
				const id = getQueryParam('id')?.trim();
				setActiveItem(id);
				scrollToElement(document.getElementById(String(id)));
			}
		}
	}, [highlightId, items]);

	const onTriggerClick = id => {
		// Close the line if we're clicking an open one
		const value = id === activeItem ? null : id;
		setActiveItem(value);
		setQueryString(id);
	};

	return (
		<StyledAccordion.Wrapper>
			{title && title.length > 0 && <H3 marginBottom="3rem">{title}</H3>}
			{items.map(
				item =>
					item.Values && (
						<StyledAccordion.AccordionLine
							id={String(item.id)}
							key={item.id}
							active={activeItem === item.id}>
							<StyledAccordion.AccordionTrigger
								active={activeItem === item.id}
								onClick={() => onTriggerClick(item.id)}>
								<span>{item.Values.PageTitle || item.Values.Title}</span>
								<StyledAccordion.IconWrapper>
									<StyledAccordion.Line1 />
									<StyledAccordion.Line2 />
								</StyledAccordion.IconWrapper>
							</StyledAccordion.AccordionTrigger>
							<AnimateHeight duration={300} height={activeItem === item.id ? 'auto' : 0}>
								<StyledAccordion.AccordionContent buttonString>
									<RichTextStyleWrapper>
										<Interweave content={item.Values.Content} />
									</RichTextStyleWrapper>
									{buttonString && (
										<StyledAccordion.AccordionButton
											primary
											onClick={() => buttonOnClick(item.urlRedirect)}>
											{buttonString}
										</StyledAccordion.AccordionButton>
									)}
								</StyledAccordion.AccordionContent>
							</AnimateHeight>
						</StyledAccordion.AccordionLine>
					)
			)}
		</StyledAccordion.Wrapper>
	);
};

export default Accordion;
